"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { workbooksApi } from "@/lib/api/apiwrapper";
import type { WorkbookDto, WorkbookItemDto } from "@/src/generated/api/src/models";
import { useAuth } from "./AuthContext";
import { ERoles } from "@/data/roles";

export interface WorkbookSummary {
  id: string;
  slug: string;
  title: string;
}

interface WorkbookContextType {
  current: WorkbookSummary | null;
  currentWorkbook: WorkbookDto | null;
  currentItems: WorkbookItemDto[];
  myWorkbooks: WorkbookSummary[];
  loading: boolean;
  select: (wb: WorkbookSummary) => void;
  clear: () => void;
  refresh: () => Promise<void>;
  scrollToWorkbook: () => void;
  viewMode: "content" | "workbook";
  setViewMode: (mode: "content" | "workbook") => void;
  scrollRequestId: number;
}

const WorkbookContext = createContext<WorkbookContextType | undefined>(undefined);

export function WorkbookProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useLocalStorage<WorkbookSummary | null>("workbooks:current", null);
  const [currentWorkbook, setCurrentWorkbook] = useState<WorkbookDto | null>(null);
  const [currentItems, setCurrentItems] = useState<WorkbookItemDto[]>([]);
  const [myWorkbooks, setMyWorkbooks] = useState<WorkbookSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<"content" | "workbook">("content");
  const [scrollRequestId, setScrollRequestId] = useState<number>(0);
  const { isInRole } = useAuth();
  const isOrganizationUser = isInRole(ERoles.OrganizationUser);

  const select = (wb: WorkbookSummary) => {
    setCurrent(wb);
  };

  const clear = () => {
    setCurrent(null);
    setCurrentWorkbook(null);
    setCurrentItems([]);
  };

  // Load full workbook details when current workbook changes
  useEffect(() => {
    const loadCurrentWorkbook = async () => {
      if (!isOrganizationUser) {
        setCurrentWorkbook(null);
        setCurrentItems([]);
        return;
      }

      if (!current?.slug) {
        setCurrentWorkbook(null);
        setCurrentItems([]);
        return;
      }

      try {
        const data = await workbooksApi.workbooksGetBySlugGET({ slug: current.slug });
        setCurrentWorkbook(data);
        const sorted = (data.items ?? []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setCurrentItems(sorted);
      } catch (e) {
        console.error("Failed to load current workbook:", e);
      }
    };

    loadCurrentWorkbook();
  }, [current?.slug]);

  const refresh = async () => {
    if (!isOrganizationUser) {
      setCurrentWorkbook(null);
      setCurrentItems([]);
      return;
    }

    try {
      setLoading(true);
      // Refresh the list of workbooks
      const list = await workbooksApi.workbooksListMineGET();
      const summaries: WorkbookSummary[] = (list || []).map((w) => ({ id: w.id!, slug: w.slug!, title: w.title ?? "Untitled" })).filter((w) => Boolean(w.id) && Boolean(w.slug)) as WorkbookSummary[];
      setMyWorkbooks(summaries);

      // Keep the current selection if it still exists
      const stillExists = summaries.find((w) => w.slug === current?.slug);
      if (current && stillExists) {
        setCurrent(stillExists);
      } else {
        setCurrent(summaries[0] ?? null);
      }

      // Refresh the current workbook details
      if (current?.slug) {
        const data = await workbooksApi.workbooksGetBySlugGET({ slug: current.slug });
        setCurrentWorkbook(data);
        const sorted = (data.items ?? []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setCurrentItems(sorted);
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollToWorkbook = () => {
    setViewMode("workbook");
    setScrollRequestId((id) => id + 1);
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <WorkbookContext.Provider
      value={{
        current,
        currentWorkbook,
        currentItems,
        myWorkbooks,
        loading,
        select,
        clear,
        refresh,
        scrollToWorkbook,
        viewMode,
        setViewMode,
        scrollRequestId,
      }}
    >
      {children}
    </WorkbookContext.Provider>
  );
}

export function useWorkbookContext() {
  const context = useContext(WorkbookContext);
  if (context === undefined) {
    throw new Error("useWorkbookContext must be used within a WorkbookProvider");
  }
  return context;
}
